import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  Modal,
  TextField,
} from '@heroui/react';
import { CellSwitch } from '@heroui-pro/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { AI_PROVIDER_SECRET_PLACEHOLDER } from '../constants';

const providerSchema = z.object({
  code: z
    .string()
    .min(1, '供应商标识不能为空')
    .max(50, '供应商标识长度不能超过50字符')
    .regex(
      /^[a-z][a-z0-9_-]*$/,
      '供应商标识建议小写英文开头，仅含字母、数字、下划线、连字符'
    ),
  name: z.string().min(1, '显示名称不能为空').max(100, '显示名称长度不能超过100字符'),
  apiKey: z.string().max(500, 'API 密钥长度不能超过500字符').optional(),
  accessKeyId: z.string().max(500, 'Access Key ID 长度不能超过500字符').optional(),
  secretAccessKey: z.string().max(500, 'Secret Access Key 长度不能超过500字符').optional(),
  baseUrl: z.string().max(255, '接口地址长度不能超过255字符').optional(),
  timeout: z.coerce.number().int('超时须为整数').min(1, '超时至少为1秒').max(600, '超时不能超过600秒'),
  logoUrl: z.string().max(255, 'Logo 地址长度不能超过255字符').optional(),
  website: z.string().max(255, '官网长度不能超过255字符').optional(),
  isEnabled: z.boolean().default(true),
});

type ProviderFormValues = z.infer<typeof providerSchema>;

export type AiProviderFormItem = {
  id?: string;
  code?: string | null;
  name?: string | null;
  hasApiKey?: boolean | null;
  hasAccessKeyId?: boolean | null;
  hasSecretAccessKey?: boolean | null;
  baseUrl?: string | null;
  timeout?: number | null;
  logoUrl?: string | null;
  website?: string | null;
  isEnabled?: boolean | null;
};

interface AiProviderEditorModalProps {
  open: boolean;
  provider?: AiProviderFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export function AiProviderEditorModal({
  open,
  provider,
  onCancel,
  onSuccess,
}: AiProviderEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!provider;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerSchema) as any,
    defaultValues: {
      code: '',
      name: '',
      apiKey: '',
      accessKeyId: '',
      secretAccessKey: '',
      baseUrl: '',
      timeout: 60,
      logoUrl: '',
      website: '',
      isEnabled: true,
    },
    mode: 'onChange',
  });
  const { reset } = form;

  const createProviderMutation = trpc.aiProvider.create.useMutation({
    onSuccess: () => {
      toast.success('AI 供应商创建成功');
      form.reset();
      onSuccess();
      void utils.aiProvider.list.invalidate();
    },
  });

  const updateProviderMutation = trpc.aiProvider.update.useMutation({
    onSuccess: () => {
      toast.success('AI 供应商更新成功');
      form.reset();
      onSuccess();
      void utils.aiProvider.list.invalidate();
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && provider) {
      reset({
        code: provider.code || '',
        name: provider.name || '',
        apiKey: provider.hasApiKey ? AI_PROVIDER_SECRET_PLACEHOLDER : '',
        accessKeyId: provider.hasAccessKeyId ? AI_PROVIDER_SECRET_PLACEHOLDER : '',
        secretAccessKey: provider.hasSecretAccessKey ? AI_PROVIDER_SECRET_PLACEHOLDER : '',
        baseUrl: provider.baseUrl || '',
        timeout: provider.timeout ?? 60,
        logoUrl: provider.logoUrl || '',
        website: provider.website || '',
        isEnabled: provider.isEnabled ?? true,
      });
    } else {
      reset({
        code: '',
        name: '',
        apiKey: '',
        accessKeyId: '',
        secretAccessKey: '',
        baseUrl: '',
        timeout: 60,
        logoUrl: '',
        website: '',
        isEnabled: true,
      });
    }
  }, [open, isEdit, provider, reset]);

  const onSubmit = async (values: ProviderFormValues) => {
    const baseUrl = values.baseUrl?.trim() || null;
    const logoUrl = values.logoUrl?.trim() || null;
    const website = values.website?.trim() || null;
    const apiKeyRaw = values.apiKey?.trim() ?? '';
    const accessKeyIdRaw = values.accessKeyId?.trim() ?? '';
    const secretAccessKeyRaw = values.secretAccessKey?.trim() ?? '';

    if (isEdit && provider?.id) {
      await updateProviderMutation.mutateAsync({
        id: provider.id,
        data: {
          code: values.code,
          name: values.name,
          apiKey: apiKeyRaw ? apiKeyRaw : null,
          accessKeyId: accessKeyIdRaw ? accessKeyIdRaw : null,
          secretAccessKey: secretAccessKeyRaw ? secretAccessKeyRaw : null,
          baseUrl,
          timeout: values.timeout,
          logoUrl,
          website,
          isEnabled: values.isEnabled,
        },
      });
    } else {
      await createProviderMutation.mutateAsync({
        code: values.code,
        name: values.name,
        apiKey: apiKeyRaw ? apiKeyRaw : undefined,
        accessKeyId: accessKeyIdRaw ? accessKeyIdRaw : undefined,
        secretAccessKey: secretAccessKeyRaw ? secretAccessKeyRaw : undefined,
        baseUrl: baseUrl ?? undefined,
        timeout: values.timeout,
        logoUrl: logoUrl ?? undefined,
        website: website ?? undefined,
        isEnabled: values.isEnabled,
      });
    }
  };

  const isLoading = createProviderMutation.isPending || updateProviderMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            key={
              open ? (isEdit && provider ? `edit-${String(provider.id ?? '')}` : 'create') : 'closed'
            }
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑 AI 供应商' : '创建 AI 供应商'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改供应商连接配置' : '填写供应商信息以创建新供应商'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-ai-provider-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.code}>
                      <Label className="text-sm font-medium">
                        供应商标识 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={50}
                        placeholder="如：openai、deepseek"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        小写英文开头，仅含字母、数字、下划线、连字符
                      </Description>
                      {errors.code ? <FieldError>{errors.code.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.name}>
                      <Label className="text-sm font-medium">
                        显示名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="请输入显示名称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="apiKey"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.apiKey}>
                      <Label className="text-sm font-medium">API 密钥</Label>
                      <Input
                        type="password"
                        maxLength={500}
                        placeholder={isEdit ? '不修改请保留占位符' : '单密钥鉴权（如 OpenAI）'}
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        单密钥体系使用；与 Access Key ID / Secret Access Key 二选一
                      </Description>
                      {errors.apiKey ? <FieldError>{errors.apiKey.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="accessKeyId"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.accessKeyId}>
                      <Label className="text-sm font-medium">Access Key ID</Label>
                      <Input
                        type="password"
                        maxLength={500}
                        placeholder={isEdit ? '不修改请保留占位符' : '双密钥鉴权 Access Key ID'}
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.accessKeyId ? (
                        <FieldError>{errors.accessKeyId.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="secretAccessKey"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.secretAccessKey}>
                      <Label className="text-sm font-medium">Secret Access Key</Label>
                      <Input
                        type="password"
                        maxLength={500}
                        placeholder="双密钥鉴权 Secret Access Key，可选"
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        编辑时留空表示清空；保留占位符表示不修改
                      </Description>
                      {errors.secretAccessKey ? (
                        <FieldError>{errors.secretAccessKey.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="baseUrl"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.baseUrl}>
                      <Label className="text-sm font-medium">接口地址</Label>
                      <Input
                        maxLength={255}
                        placeholder="为空时使用 SDK 默认地址"
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.baseUrl ? <FieldError>{errors.baseUrl.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="timeout"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.timeout}>
                      <Label className="text-sm font-medium">
                        超时（秒） <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        max={600}
                        placeholder="60"
                        variant="secondary"
                        value={String(field.value ?? '')}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.timeout ? <FieldError>{errors.timeout.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.logoUrl}>
                      <Label className="text-sm font-medium">Logo 地址</Label>
                      <Input
                        maxLength={255}
                        placeholder="可选"
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.logoUrl ? <FieldError>{errors.logoUrl.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.website}>
                      <Label className="text-sm font-medium">官网</Label>
                      <Input
                        maxLength={255}
                        placeholder="可选"
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.website ? <FieldError>{errors.website.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">是否启用</Label>
                  <Controller
                    control={form.control}
                    name="isEnabled"
                    render={({ field }) => (
                      <CellSwitch
                        aria-label="是否启用"
                        variant="secondary"
                        isSelected={!!field.value}
                        onChange={field.onChange}
                      >
                        <CellSwitch.Trigger>
                          <CellSwitch.Label>
                            {field.value ? '已启用' : '已停用'}
                          </CellSwitch.Label>
                          <CellSwitch.Control />
                        </CellSwitch.Trigger>
                      </CellSwitch>
                    )}
                  />
                  <Description className="text-xs">停用后禁止新调用</Description>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
